import { getDatabase, ref, update } from 'firebase/database';
import { BlueprintLocation } from '../types/protocols';
import { requireAdmin } from './adminGuard';
import { softDeleteDrawing } from './fileDeletionApi';
import { recordActivity } from './activityApi';

/** One marker to write: the entity node path (no leading slash) and its position on the new drawing. */
export interface RelocationPlacement {
    dbPath: string;
    location: BlueprintLocation;
}

export interface CommitBlueprintRelocationOptions {
    /** Key of the uploaded drawing; every placement is written under `blueprintLocations/{imageKey}`. */
    imageKey: string;
    /**
     * Root-relative DB writes that make the uploaded drawing current: the
     * owner's `currentBlueprintKey` and, for parking areas, `blueprintSource`.
     * Returned by BlueprintUploadModal when `deferPointer` is set.
     */
    pointerUpdates: Record<string, unknown>;
    placements: RelocationPlacement[];
}

/**
 * Makes an uploaded drawing current and writes every marker placed on it in
 * ONE multi-path update at the DB root. Positions on older drawings are never
 * touched. With no placements this is exactly the pointer switch the upload
 * modal would otherwise have done itself.
 */
export const commitBlueprintRelocation = async (options: CommitBlueprintRelocationOptions): Promise<void> => {
    const db = getDatabase();
    await requireAdmin(db, 'byta ritning');
    const updates: Record<string, unknown> = { ...options.pointerUpdates };
    for (const { dbPath, location } of options.placements) {
        updates[`${dbPath}/blueprintLocations/${options.imageKey}`] = {
            key: options.imageKey,
            x: location.x,
            y: location.y,
            width: location.width,
            height: location.height,
        };
    }
    await update(ref(db), updates);
    const pointerPath = Object.keys(options.pointerUpdates)[0] ?? '';
    recordActivity('drawing.updated', { buildingKey: /^buildings\/([^/]+)\//.exec(pointerPath)?.[1], targetKey: options.imageKey });
};

export interface AbortBlueprintRelocationOptions {
    /** DB path of the uploaded (never made current) drawing node, e.g. `buildings/{b}/structure/levels/{l}/blueprints/{k}`. */
    blueprintPath: string;
    /** Tombstone entity type, e.g. `levelBlueprint` or `parkingBlueprint`. */
    entityType: string;
}

/**
 * The user aborted the wizard: the drawing was uploaded but never made
 * current, so it is tombstoned (soft delete, both blobs kept: raster and PDF
 * source) and nothing else changes.
 */
export const abortBlueprintRelocation = async (options: AbortBlueprintRelocationOptions): Promise<void> => {
    await requireAdmin(getDatabase(), 'byta ritning');
    await softDeleteDrawing({ originalPath: options.blueprintPath, entityType: options.entityType });
};
